// ===========================================
// Email Unsubscribe hooks (PUBLIC — magic link)
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emailUnsubscribeApi } from '../api/emailUnsubscribe.api';

export const emailUnsubscribeKeys = {
  detail: (token: string) => ['email-unsubscribe', token] as const,
};

/**
 * Load the confirm page.
 *
 * ⚠️ `retry: false`. The only failure this route produces is a uniform 404
 * (unknown token, malformed token, deleted school — deliberately
 * indistinguishable). Retrying spends the token's rate-limit budget three more
 * times for an outcome that cannot change, and that budget is shared with the
 * POST the parent is about to make.
 *
 * `refetchOnWindowFocus: false` for the same reason: a page left open on a
 * phone would otherwise re-fetch on every app switch.
 */
export function useUnsubscribeDetails(token: string) {
  return useQuery({
    queryKey: emailUnsubscribeKeys.detail(token),
    queryFn: () => emailUnsubscribeApi.get(token),
    enabled: Boolean(token),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

/**
 * Perform the opt-out.
 *
 * ⚠️ `retry: false` again, and here it is load-bearing rather than tidy: the
 * limiter allows 10 writes per token per 15 minutes, so a retrying mutation
 * turns one impatient double-tap into 8 spent attempts. The request is
 * idempotent server-side, so a retry could never repair anything anyway.
 *
 * ⚠️ WRITES the result into the detail cache rather than invalidating it. The
 * distinction matters because of `staleTime: Infinity` above: an invalidate
 * would mark the entry stale but a remount would still render the STALE cached
 * value first, and `setQueryData` is the only form that fixes the actual bug.
 *
 * The bug, measured: unsubscribe, navigate away, come back in the same tab
 * (browser back, or re-clicking the link from a mail client that reuses the
 * SPA shell). The component remounts, so local state resets, and the cache
 * still says `alreadyUnsubscribed: false` — so a parent who HAS unsubscribed
 * is shown the "Unsubscribe?" button again. Harmless server-side (the POST is
 * idempotent), but it tells them their opt-out did not take, which is the one
 * message this page must never send.
 *
 * Still no refetch: the POST response carries the same shape as the GET, so
 * seeding the cache with it costs nothing.
 */
export function useUnsubscribe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => emailUnsubscribeApi.unsubscribe(token),
    retry: false,
    onSuccess: (data, token) => {
      queryClient.setQueryData(emailUnsubscribeKeys.detail(token), data);
    },
  });
}
